import React, { useEffect, useMemo, useRef, useState } from "react";
import { playGameSound } from "../../services/gameSounds";
import { motion } from "framer-motion";
import { launchConfetti } from "../../utils/confetti";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebaseConfig";
import { buildReturnPath } from "../../utils/buildReturnPath";
import "./CountPop.css";
import { useLeaveGame } from "./gameExit";
import "./kit/quizkit.css"; // .qk-scene: the shared game background (no pirate ship — that's the bonus round's)
import "./kit/classicGames.css"; // the new look for this game (buttons, cards, score chips)

const DEFAULT_BG_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fsky-background-01.jpg?alt=media&token=5bfad38f-263b-4e2e-a3c9-52b5b0e6e001";

// WAS: sounds%2Fpop-01.mp3, token 7c6e9b9b-...-3b2d8b8d1abc
// WAS: sounds%2Fwrong-01.mp3, token 6a7a2a60-...-87d8a64a1c33
//
// Neither file exists in Storage. Both URLs appeared exactly once in the
// whole codebase — only here — while every sound that is really uploaded is
// referenced from a dozen files or more. A 404 from Storage returns an XML
// error body, not audio, so the <audio> element rejects it and .play()
// rejects with "The element has no supported sources", which surfaced as an
// error the moment a child popped their first balloon.
//
// Pointed at the two sounds the rest of the app already uses and that are
// therefore known to exist: bongsound1 for a correct pop, tapsound1 for a
// wrong one. If you later upload a purpose-made pop, swap the URL here — but
// upload it first and load the URL in a browser tab before pasting it in.
const POP_SFX =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";

const BUZZ_SFX =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686";

// Balloon colours.
//
// Every balloon used to be the same pink (#ff6fae), which made a screen of
// them hard to tell apart at a glance — the child has to read each number
// rather than track shapes.
//
// The constraint is that the NUMBER has to stay readable, and the number is
// white. That rules out most balloon-looking colours: the old pink measured
// 2.58:1 against white, and cheerful yellow (#f08c00) is 2.48:1 — both below
// even the 3:1 floor for large text. Every colour below was measured against
// #ffffff and clears 4.5:1, the full WCAG AA threshold, so the numbers hold
// up for a child with low vision and on a washed-out projector at the back
// of a classroom.
//
// If you add a colour here, measure it first. A balloon that looks lovely on
// a laptop can put the number out of reach on a smartboard.
const BALLOON_COLORS = [
  { hex: "#e03131", name: "red" },     // 4.51:1
  { hex: "#d6336c", name: "pink" },    // 4.62:1
  { hex: "#9c36b5", name: "purple" },  // 5.82:1
  { hex: "#6741d9", name: "violet" },  // 6.30:1
  { hex: "#1971c2", name: "blue" },    // 5.02:1
  { hex: "#0b7285", name: "cyan" },    // 5.59:1
  { hex: "#087f5b", name: "teal" },    // 5.00:1
];

  const MUSIC_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fbonus-round1.mp3?alt=media&token=e4c606bb-90ca-4a78-9f2e-34a709084464";

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";

const DEFAULT_MAX_NUMBER = 20;

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

let idSeed = 0;

export default function CountPop({
  onComplete,
  questions = [],
  topic,
  placeholderImage,
}) {
  const navigate = useNavigate();
  const leaveGame = useLeaveGame(); // back to the subject page this game came from
  const { subject } = useParams();

  const settings = questions?.[0] || {};

  const maxNumber = Number(settings.maxNumber || DEFAULT_MAX_NUMBER);
  const pointsPerCorrect = Number(settings.pointsPerCorrect || 5);
  const wrongPenalty = Number(settings.wrongPenalty || 3);

  const [score, setScore] = useState(0);
  const [target, setTarget] = useState(0);
  const [balloons, setBalloons] = useState([]);
  const [details, setDetails] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [message, setMessage] = useState("Pop the balloons in order!");
  const [isMuted, setIsMuted] = useState(false);

  const popAudio = useMemo(() => new Audio(POP_SFX), []);
  const buzzAudio = useMemo(() => new Audio(BUZZ_SFX), []);

  // A sound effect must never be able to interrupt the game.
  //
  // HTMLMediaElement.play() returns a promise that REJECTS on a missing file,
  // an unsupported codec, or a browser that has not yet seen a user gesture.
  // An unhandled rejection from a bare .play() is what put "The element has
  // no supported sources" on screen. A child who cannot hear a pop still has
  // a working game; a child staring at an error does not.
  const playSafely = (audio) => {
    if (!audio) return;
    try {
      audio.currentTime = 0;
      const played = audio.play();
      if (played && typeof played.catch === "function") {
        played.catch((error) => {
          console.warn("CountPop sound could not play:", error?.message || error);
        });
      }
    } catch (error) {
      console.warn("CountPop sound could not play:", error?.message || error);
    }
  };
  const musicRef = useRef(null);
  const lastTargetSpawnRef = useRef(Date.now());

  useEffect(() => {
    if (!MUSIC_URL) return;
  
    musicRef.current = new Audio();
    musicRef.current.src = MUSIC_URL;
    musicRef.current.loop = true;
    musicRef.current.volume = 0.25;
  
    musicRef.current.play().catch((error) => {
      console.warn("CountPop music could not play:", error);
    });
  
    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.removeAttribute("src");
        musicRef.current.load();
      }
    };
  }, []);

  useEffect(() => {
    if (completed) return;

    const interval = setInterval(() => {
      setBalloons((prev) => {
        const next = [...prev];
        const capacity = Math.max(0, 9 - next.length);
        const toSpawn = Math.min(capacity, randInt(1, 2));

        if (toSpawn === 0) return next;

        for (let i = 0; i < toSpawn; i++) {
          const mustSpawnTarget =
            Date.now() - lastTargetSpawnRef.current > 2500;

          const value =
            mustSpawnTarget || Math.random() < 0.45
              ? target
              : randInt(0, maxNumber);

          if (value === target) {
            lastTargetSpawnRef.current = Date.now();
          }

          // Colour is chosen at spawn and stored on the balloon, not derived
          // at render time. Deriving it would re-roll on every re-render and
          // the balloon would flicker through the palette as it fell.
          //
          // Keyed off the id rather than Math.random() so the colours cycle
          // evenly instead of clumping — three reds in a row is exactly the
          // case that makes a child think two balloons are the same one.
          const color = BALLOON_COLORS[idSeed % BALLOON_COLORS.length];

          next.push({
            id: ++idSeed,
            value,
            x: randInt(7, 93),
            speed: randInt(6, 11),
            size: randInt(70, 95),
            color: color.hex,
            colorName: color.name,
          });
        }

        return next;
      });
    }, 900);

    return () => clearInterval(interval);
  }, [target, completed, maxNumber]);

  const removeBalloon = (id) => {
    setBalloons((prev) => prev.filter((balloon) => balloon.id !== id));
  };

  const finishGame = (finalScore, finalDetails) => {
    if (completed) return;

    setCompleted(true);
    launchConfetti();

    onComplete?.({
      score: finalScore,
      answers: finalDetails.map((item) => ({
        question: `Pop number ${item.expected}`,
        selectedText: String(item.clicked),
        correctText: String(item.expected),
        correct: item.correct,
      })),
      details: finalDetails,
    });
  };

  const handlePop = (balloon) => {
    if (completed) return;

    const correct = balloon.value === target;

    const event = {
      step: target,
      expected: target,
      clicked: balloon.value,
      correct,
      timestamp: new Date().toISOString(),
    };

    const nextDetails = [...details, event];
    setDetails(nextDetails);
    removeBalloon(balloon.id);

    if (correct) {
      playGameSound("correct", "countPop", () => {
        try {
          playSafely(popAudio);
        } catch {}
      });

      const nextScore = score + pointsPerCorrect;
      const nextTarget = target + 1;

      setScore(nextScore);
      setMessage(`Great! Now pop ${nextTarget <= maxNumber ? nextTarget : "done"}!`);

      if (target >= maxNumber) {
        finishGame(nextScore, nextDetails);
      } else {
        setTarget(nextTarget);
      }
    } else {
      playGameSound("wrong", "countPop", () => {
        try {
          playSafely(buzzAudio);
        } catch {}
      });

      const nextScore = Math.max(0, score - wrongPenalty);
      setScore(nextScore);
      setMessage(`Oops! You need ${target}. Keep going!`);
    }
  };

  const handleReturnHome = () => leaveGame();

  const revealed = useMemo(() => {
    const arr = [];

    for (let n = 0; n <= maxNumber; n++) {
      arr.push(n < target);
    }

    return arr;
  }, [target, maxNumber]);

  return (
    <div
      className="count-pop-wrapper qk-scene"
    >
      <img src={LOGO_URL} alt="SBL Logo" className="countpop-logo" />

      <button
        type="button"
        className="countpop-mute-button"
        onClick={() => {
          const nextMuted = !isMuted;
          setIsMuted(nextMuted);

          if (musicRef.current) {
            musicRef.current.muted = nextMuted;
          }
        }}
      >
        {isMuted ? "🔇" : "🔊"}
      </button>

      <button
        type="button"
        className="countpop-return-button"
        onClick={handleReturnHome}
      >
        ⬅ Return
      </button>

      <div className="countpop-title-card">
        <h1>🎈 Count Pop</h1>
        <p>Pop the balloons in order from 0 to {maxNumber}!</p>
        <h2>Next Number: {target}</h2>
        <div className="countpop-message">{message}</div>
      </div>

      <div className="sequence-bar">
        {Array.from({ length: maxNumber + 1 }, (_, n) => (
          <div
            key={n}
            className={`seq-pill ${revealed[n] ? "revealed" : "masked"}`}
          >
            {n}
          </div>
        ))}
      </div>

      <div className="hud">
        <div className="hud-item">⭐ Score: {score}</div>
        <div className="hud-item">
          🎯 Pop next: <strong>{target}</strong>
        </div>
      </div>

      <div className="playfield">
        {balloons.map((balloon) => (
          <motion.button
            key={balloon.id}
            className="balloon"
            style={{
              left: `${balloon.x}vw`,
              width: balloon.size,
              height: balloon.size * 1.25,
              // Fed to the stylesheet as a custom property so the balloon's
              // highlight, shadow and knot gradients all stay in one place in
              // the CSS instead of being rebuilt inline per balloon.
              "--balloon-color": balloon.color || "#1971c2",
            }}
            initial={{ y: -170, scale: 0.95 }}
            animate={{ y: "110vh", scale: 1 }}
            transition={{ duration: balloon.speed, ease: "linear" }}
            onAnimationComplete={() => removeBalloon(balloon.id)}
            onClick={() => handlePop(balloon)}
            whileTap={{ scale: 0.85 }}
            aria-label={`${balloon.colorName || "Coloured"} balloon, number ${balloon.value}`}
          >
            <span className="balloon-number">{balloon.value}</span>
            <span className="balloon-string" />
          </motion.button>
        ))}
      </div>

      <div className="cp-footer">
        <button
          className="cp-btn ok"
          type="button"
          onClick={() => {
            setScore(0);
            setTarget(0);
            setBalloons([]);
            setDetails([]);
            setCompleted(false);
            setMessage("Pop the balloons in order!");
          }}
        >
          Play Again
        </button>

        <button className="cp-btn home" type="button" onClick={handleReturnHome}>
          Home
        </button>

        <div className="copyright">© Smart Board Learning LLC</div>
      </div>
    </div>
  );
}